<template>
  <div>
    <Head title="Roles" />
    <h1 class="mb-8 text-3xl font-bold">
      Roles
    </h1>
    <div class="flex flex-col md:flex-row md:space-x-6">
      <div class="mb-6 md:mb-0 md:w-1/4">
        <div class="bg-white rounded-md shadow overflow-hidden">
          <button
            v-for="role in roles"
            :key="role.id"
            class="block px-6 py-4 w-full text-left focus:bg-gray-100 border-b border-gray-100 focus:outline-none"
            :class="selectedRoleId === role.id ? 'bg-indigo-50' : 'hover:bg-gray-50'"
            type="button"
            @click="selectRole(role)">
            <span :class="selectedRoleId === role.id ? 'font-bold' : ''">
              {{ role.name }}
            </span>
            <span class="block text-gray-500 text-xs"> {{ role.permission_ids.length }} permissions </span>
          </button>
          <div v-if="roles.length === 0" class="px-6 py-4 text-gray-500 text-sm">
            No roles defined. Roles are provisioned by seeders.
          </div>
        </div>
      </div>
      <div v-if="selectedRole" class="flex-1">
        <div class="mb-4 text-gray-500 text-sm">
          Toggles apply immediately — there is no save step.
        </div>
        <div v-if="error" class="mb-4 text-red-600 text-sm">
          {{ error }}
        </div>
        <div
          v-for="group in permissionGroups"
          :key="group.label"
          class="mb-6 bg-white rounded-md shadow">
          <div class="px-6 py-3 font-bold border-b border-gray-100">
            {{ group.label }}
          </div>
          <div class="grid gap-x-6 grid-cols-1 p-6 md:grid-cols-2">
            <label
              v-for="permission in group.permissions"
              :key="permission.id"
              class="flex items-center py-1">
              <input
                type="checkbox"
                class="form-checkbox"
                :checked="isChecked(permission)"
                :disabled="isToggling(permission)"
                @change="toggle(permission)">
              <span class="ml-2 text-sm" :class="{ 'opacity-50': isToggling(permission) }">
                {{ permission.name }}
              </span>
            </label>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { Head } from '@inertiajs/vue3'
import Layout from '@/Shared/Layout'

export default {
    components: {
        Head,
    },
    layout: Layout,
    props: {
        roles: Array,
        permissions: Array,
    },
    data () {
        return {
            selectedRoleId: this.roles.length ? this.roles[0].id : null,
            togglingIds: [],
            error: '',
        }
    },
    computed: {
        selectedRole () {
            return this.roles.find((role) => role.id === this.selectedRoleId) || null
        },
        permissionGroups () {
            const named = {}
            const global = []
            for (const permission of this.permissions) {
                const dot = permission.name.indexOf('.')
                const head = dot === -1 ? permission.name : permission.name.slice(0, dot)
                if (head === '*') {
                    global.push(permission)
                } else {
                    if (!named[head]) named[head] = []
                    named[head].push(permission)
                }
            }
            const groups = Object.keys(named)
                .sort()
                .map((label) => ({ label, permissions: named[label] }))
            if (global.length) groups.unshift({ label: 'Global Wildcards', permissions: global })
            return groups
        },
    },
    methods: {
        selectRole (role) {
            this.selectedRoleId = role.id
            this.error = ''
        },
        isChecked (permission) {
            return this.selectedRole ? this.selectedRole.permission_ids.includes(permission.id) : false
        },
        isToggling (permission) {
            return this.togglingIds.includes(permission.id)
        },
        toggle (permission) {
            if (!this.selectedRole || this.isToggling(permission)) return
            this.togglingIds.push(permission.id)
            this.error = ''
            this.$inertia.patch(
        `/roles/${this.selectedRole.id}/permissions/${permission.id}`,
        {},
        {
            preserveScroll: true,
            preserveState: true,
            only: ['roles'],
            onFinish: () => {
                this.togglingIds = this.togglingIds.filter((id) => id !== permission.id)
            },
            onError: () => {
                this.error = 'Could not save the change — the checkbox shows the stored state.'
            },
        },
            )
        },
    },
}
</script>
